import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import {
  getItemDisabledErrorMessage,
  KNOWN_RESPONSE_MESSAGES,
  KNOWN_RESPONSE_STATUSES,
} from 'api/const';
import {
  useCalculateStormRate,
  useCreateStormRate,
  useGetStormRateById,
} from 'api/rates';
import { StyledButton } from 'components/Buttons';
import { StyledTextField } from 'components/Inputs';
import _, { debounce } from 'lodash';
import {
  useForm,
  Controller,
  SubmitHandler,
  useFieldArray,
} from 'react-hook-form';
import {
  useNavigate,
  generatePath,
  useParams,
  useLocation,
} from 'react-router-dom';
import { RatesTab, reactPaths } from 'root/pathnames';
import { RatesType } from 'root/pathnames/tabs';
import { appToast } from 'utils/toast.utils';
import ActivityAutocomplete from './elements/ActivityAutocomplete';
import CommodityAutocomplete from './elements/CommodityAutocomplete';
import ContainerTypeAutocomplete from './elements/ContainerTypeAutocomplete';
import CurrencyAutocomplete from './elements/CurrencyAutocomplete';
import NamedAccountAutocomplete from './elements/NamedAccountAutocomplete';
import RouteFromAutocomplete from './elements/RouteFromAutocomplete';
import RouteToAutocomplete from './elements/RouteToAutocomplete';
import ShipownerAutocomplete from './elements/ShipownerAutocomplete';
import TradeTypeAutocomplete from './elements/TradeTypeAutocomplete';

import UnitAutocomplete from './elements/UnitAutocomplete';

import {
  ACTIVITY_LABEL,
  ADD_NEW_ACTIVITY_BUTTON,
  ADD_RATE_BUTTON,
  CANCEL_BUTTON,
  COMMODITY_LABEL,
  CONTAINER_TYPE_LABEL,
  CONTRACT_NUMBER_LABEL,
  CREATE_ERROR_TOAST,
  CREATE_SUCCESS_TOAST,
  DAYS_OFF_LABEL,
  EXPIRATION_DATE_FROM_LABEL,
  EXPIRATION_DATE_TO_LABEL,
  FIELD_MIN_ZERO_ERROR,
  FIELD_REQUIRED_ERROR,
  FORM_TITLE,
  INTERNAL_NOTE_LABEL,
  NAMED_ACCOUNT_LABEL,
  RATE_ALL_IN_LABEL,
  ROUTE_FROM_LABEL,
  ROUTE_TO_LABEL,
  SEA_FREIGHT,
  SHIPOWNER_LABEL,
  RELATION_LABEL,
  COST_LABEL,
  NULL_VALUE,
} from './RatesForm.consts';
import * as S from './RatesForm.css';
import { fieldIsRequired, getIds, isSuccessful } from './ratesForm.utils';
import {
  expirationDateToValidationRules,
  namedAccountValidationRules,
  routeFromValidationRules,
  routeToValidationRules,
} from './ratesFormValidators';
import {
  FormInputs,
  LocationState,
  ratesFormPayload,
  StormRateJobsItem,
} from './types';

const debouncedCalculateAllIn = debounce((fn) => fn(), 1000);
const debouncedTrigger = debounce((fn) => fn(), 100);

export const RatesForm = () => {
  const location = useLocation();
  const state = location.state as LocationState | null;
  const rateId = state?.rateId;

  const navigate = useNavigate();
  const { tab: ratesTab } = useParams<{ tab: RatesTab }>();
  const { createStormRate } = useCreateStormRate();
  const { data: rateData, getStormRateById } = useGetStormRateById();
  const { calculateStormRate } = useCalculateStormRate();

  const {
    control,
    register,
    handleSubmit,
    watch,
    trigger,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'all',
    shouldUnregister: true,
    defaultValues: {
      activities: rateId
        ? []
        : [
            {
              activity: { id: 2, message: SEA_FREIGHT, value: 2 },
              cost: undefined,
              currency: null,
            },
          ],
      commodity: [],
      containerType: null,
      contractNumber: '',
      daysOff: '',
      expirationDateFrom: '',
      expirationDateTo: '',
      namedAccount: [],
      rateAllIn: 0,
      routeFrom: [],
      routeTo: [],
      shipowner: null,
      internalNote: '',
      tradeType: rateId ? null : { name: 'EXPORT' },
    },
  });

  const [allInValue, setAllInValue] = useState('0');
  const [freightCurrency, setFreightCurrency] = useState('');
  const [currencyChanged, setCurrencyChanged] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] =
    useState<boolean>(false);

  const expirationDateFromValue = watch('expirationDateFrom');
  const expirationDateToValue = watch('expirationDateTo');
  const routeToValues = watch('routeTo');
  const routeFromValues = watch('routeFrom');
  const activitiesValues = watch('activities');
  const shipownerValue = watch('shipowner');
  const commodityValue = watch('commodity');
  const namedAccount = watch('namedAccount');
  const containerTypeValue = watch('containerType');
  const tradeTypeValue = watch('tradeType');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'activities',
  });

  const currencyChangeHandler = () => {
    calculateAllIn();
  };

  const addNewActivityHandler = () => {
    append({
      activity: null,
      cost: undefined,
      currency: null,
    });
    trigger();
  };

  const removeActivityHandler = (index: number) => () => {
    remove(index);
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (data) {
      const ratesFormPayload: ratesFormPayload = {
        shippingLineId: data.shipowner ? getIds(data.shipowner) : null,
        startPortsIds: getIds(data.routeFrom),
        endPortsIds: getIds(data.routeTo),
        commodityIds: data.commodity ? getIds(data.commodity) : null,
        containerId: data.containerType ? getIds(data.containerType) : null,
        tradeType: data?.tradeType?.name ? data?.tradeType?.name : null,
        dateFrom: data.expirationDateFrom
          ? new Date(data.expirationDateFrom).toISOString()
          : null,
        dateTo: data.expirationDateTo
          ? new Date(data.expirationDateTo).toISOString()
          : null,
        daysOff: data.daysOff || '',
        note: data.internalNote || '',
        contractNumber: data.contractNumber.trim(),
        namedAccountIds: data.namedAccount
          ? getIds(data.namedAccount).filter((id: null) => id !== null)
          : [],
        stormRateJobs: data.activities.map((item: StormRateJobsItem) => ({
          activityId: item.activity.id,

          currencyAmount: {
            currencyCode: { id: item.currency.id },
            value: item.cost,
          },
          unitId: item.unit.id,
        })),
      };

      setSubmitButtonDisabled(true);
      try {
        const res = await createStormRate(ratesFormPayload);
        if (res.status && isSuccessful(res)) {
          appToast.success(CREATE_SUCCESS_TOAST);
          navigate(generatePath(reactPaths.ratesModule, { tab: ratesTab }));
        } else if (
          res.data.status === KNOWN_RESPONSE_STATUSES.BAD_REQUEST &&
          res.data.message === KNOWN_RESPONSE_MESSAGES.ITEM_DISABLED
        ) {
          const { type, code } = res.data.result;
          const errMsg = getItemDisabledErrorMessage({ type, code });
          appToast.error(errMsg);
        } else {
          appToast.error(CREATE_ERROR_TOAST);
        }
      } catch (error) {
        appToast.error(CREATE_ERROR_TOAST);
      } finally {
        setSubmitButtonDisabled(false);
      }
    }
  };

  const cancelButtonHandler = () => {
    rateId
      ? navigate(
          generatePath(reactPaths.ratesDetails, {
            ratesType: RatesType.RATES,
            ratesId: String(rateId),
            tab: ratesTab,
          }),
        )
      : navigate(
          generatePath(reactPaths.ratesModule, {
            tab: ratesTab,
          }),
        );
  };

  const calculateAllIn = () => {
    debouncedCalculateAllIn(() => {
      const currencyAmounts = getValues('activities').map(
        (item: { cost: number; currency: { id: number } }) => ({
          cost: item.cost,
          currency: item.currency,
        }),
      );
      if (currencyAmounts.length > 0) {
        setFreightCurrency(currencyAmounts[0]?.currency?.currency);
        calculateStormRate({ currencyAmounts })
          .then((res) => setAllInValue(res.data.result))
          .catch((error) => console.error(error));
      }
    });
  };

  const getRateById = async (id: number) => {
    try {
      await getStormRateById(String(id));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const populateForm = (rateData: any) => {
    if (rateData) {
      setValue('shipowner', rateData.shippingLine || null);
      setValue('commodity', rateData.commodities || []);
      setValue('contractNumber', rateData?.contractNumber || '');
      setValue('daysOff', rateData?.daysOff || '');
      setValue('expirationDateFrom', rateData?.dateFrom || '');
      setValue('expirationDateTo', rateData?.dateTo || '');
      setValue(
        'namedAccount',
        rateData?.namedAccounts?.length
          ? rateData.namedAccounts
          : [{ id: null, code: NULL_VALUE, enabled: true }],
      );
      setValue('routeFrom', [rateData?.startPort]);
      setValue('routeTo', [rateData?.endPort]);
      setValue(
        'tradeType',
        rateData.tradeType ? { name: rateData?.tradeType } : { name: 'EXPORT' },
      );
      setValue('containerType', rateData?.container);
      setValue('internalNote', rateData.note || '');
      const activities = rateData.stormRateJobs
        ? [...rateData.stormRateJobs]
            .sort((a, b) => {
              if (a.activity.id === 2 && b.activity.id !== 2) {
                return -1;
              } else if (a.activity.id !== 2 && b.activity.id === 2) {
                return 1;
              }
              return 0;
            })
            .map((job: any) => ({
              activity: job.activity,
              cost: job.currencyAmount?.value,
              currency: job.currencyAmount?.currencyCode,
              unit: job.unit,
            }))
        : [];
      setValue('activities', activities);
    }
  };

  useEffect(() => {
    calculateAllIn();
  }, [activitiesValues.length]);

  useEffect(() => {
    rateId && getRateById(rateId);
    debouncedTrigger(() => {
      trigger();
    });
  }, []);

  useEffect(() => {
    populateForm(rateData);
  }, [rateData]);

  useEffect(() => {
    debouncedTrigger(() => {
      trigger();
    });
  }, [
    expirationDateFromValue,
    expirationDateToValue,
    routeToValues,
    routeFromValues,
    activitiesValues,
    containerTypeValue,
    tradeTypeValue,
  ]);

  useEffect(() => {
    if (!freightCurrency) return;
    setCurrencyChanged(true);
    const t = setTimeout(() => setCurrencyChanged(false), 400);
    return () => clearTimeout(t);
  }, [freightCurrency]);

  return (
    <Box display='flex' flexDirection='column'>
      <S.Form onSubmit={handleSubmit(onSubmit)}>
        <S.Title>{FORM_TITLE}</S.Title>
        <S.FieldGroup>
          <S.FieldLabel>{SHIPOWNER_LABEL}</S.FieldLabel>
          <S.InputWrapper>
            <Controller
              name='shipowner'
              control={control}
              render={(props) => (
                <ShipownerAutocomplete
                  formProps={props}
                  error={!!errors.shipowner}
                />
              )}
            />
            <S.ErrorWrapper>
              {errors.shipowner && <S.Error>{FIELD_REQUIRED_ERROR}</S.Error>}
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='contractNumber'>
            {CONTRACT_NUMBER_LABEL}
          </S.FieldLabel>
          <S.InputWrapper>
            <StyledTextField
              id='contractNumber'
              padding='dense'
              size='small'
              variant='outlined'
              width={280}
              error={!!errors.contractNumber}
              {...register('contractNumber', {})}
            />
            <S.ErrorWrapper>
              {errors.contractNumber && (
                <S.Error>
                  {errors?.contractNumber?.message || FIELD_REQUIRED_ERROR}
                </S.Error>
              )}
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{COMMODITY_LABEL}</S.FieldLabel>
          <S.InputWrapper>
            <Controller
              name='commodity'
              control={control}
              render={(props) => (
                <CommodityAutocomplete
                  formProps={props}
                  error={!!errors.commodity}
                />
              )}
            />
            <S.ErrorWrapper>
              {errors.commodity && <S.Error>{FIELD_REQUIRED_ERROR}</S.Error>}
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{NAMED_ACCOUNT_LABEL}</S.FieldLabel>
          <S.InputWrapper>
            <Controller
              name='namedAccount'
              control={control}
              rules={namedAccountValidationRules}
              render={(props) => (
                <NamedAccountAutocomplete
                  formProps={props}
                  error={!!errors.namedAccount}
                />
              )}
            />
            <S.Error>
              {(errors.namedAccount &&
                (Array.isArray(errors.namedAccount)
                  ? errors.namedAccount[0]?.message
                  : errors.namedAccount.message)) ||
                (errors.namedAccount && FIELD_REQUIRED_ERROR)}
            </S.Error>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{RELATION_LABEL}</S.FieldLabel>
          <S.InputWrapper>
            <Controller
              name='tradeType'
              control={control}
              render={(props) => (
                <TradeTypeAutocomplete
                  formProps={props}
                  error={!!errors.tradeType}
                />
              )}
            />
            <S.ErrorWrapper>
              {errors.tradeType && <S.Error>{FIELD_REQUIRED_ERROR}</S.Error>}
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{ROUTE_FROM_LABEL}</S.FieldLabel>
          <S.InputWrapper>
            <Controller
              name='routeFrom'
              control={control}
              rules={routeFromValidationRules(routeToValues)}
              render={(props) => (
                <RouteFromAutocomplete
                  formProps={props}
                  error={!!errors.routeFrom}
                  disabledValues={routeToValues}
                />
              )}
            />
            <S.ErrorWrapper>
              <S.Error>
                {(errors.routeFrom &&
                  (Array.isArray(errors.routeFrom)
                    ? errors.routeFrom[0]?.message
                    : errors.routeFrom.message)) ||
                  (errors.routeFrom && FIELD_REQUIRED_ERROR)}
              </S.Error>
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel>{ROUTE_TO_LABEL}</S.FieldLabel>
          <S.InputWrapper>
            <Controller
              name='routeTo'
              control={control}
              rules={routeToValidationRules(routeFromValues)}
              render={(props) => (
                <RouteToAutocomplete
                  formProps={props}
                  error={!!errors.routeTo}
                  disabledValues={routeFromValues}
                />
              )}
            />
            <S.ErrorWrapper>
              <S.Error>
                {(errors.routeTo &&
                  (Array.isArray(errors.routeTo)
                    ? errors.routeTo[0]?.message
                    : errors.routeTo.message)) ||
                  (errors.routeTo && FIELD_REQUIRED_ERROR)}
              </S.Error>
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='containerType'>
            {CONTAINER_TYPE_LABEL}
          </S.FieldLabel>
          <S.InputWrapper>
            <Controller
              name='containerType'
              control={control}
              render={(props) => (
                <ContainerTypeAutocomplete
                  formProps={props}
                  error={!!errors.containerType}
                />
              )}
            />
            <S.ErrorWrapper>
              {errors.containerType && (
                <S.Error>{FIELD_REQUIRED_ERROR}</S.Error>
              )}
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        {fields.map((_, index) => (
          <S.FieldGroup key={index} style={{ margin: '6px 0' }}>
            {index === 0 && <S.FieldLabel>{ACTIVITY_LABEL}</S.FieldLabel>}
            <S.InputWrapper
              sx={{
                gridColumn: index === 0 ? 'span 1' : '2 / span 1',
              }}
            >
              <Controller
                name={`activities[${index}].activity` as ['activities'][number]}
                control={control}
                rules={fieldIsRequired}
                render={(props) =>
                  index === 0 ? (
                    <StyledTextField
                      label={SEA_FREIGHT}
                      padding='dense'
                      size='small'
                      variant='outlined'
                      type='number'
                      width={280}
                      disabled
                      error={!!errors.activities?.[index]?.activity}
                    />
                  ) : (
                    <ActivityAutocomplete
                      formProps={props}
                      error={!!errors.activities?.[index]?.activity}
                    />
                  )
                }
              />
              <S.ErrorWrapper>
                {errors.activities?.[index]?.activity && (
                  <S.Error>{FIELD_REQUIRED_ERROR}</S.Error>
                )}
              </S.ErrorWrapper>
            </S.InputWrapper>
            <S.InputWrapper>
              <Controller
                name={`activities[${index}].unit` as ['activities'][number]}
                control={control}
                rules={fieldIsRequired}
                render={(props) => (
                  <UnitAutocomplete
                    formProps={props}
                    error={!!errors.activities?.[index]?.unit}
                  />
                )}
              />
              <S.ErrorWrapper>
                {errors.activities?.[index]?.unit && (
                  <S.Error>{FIELD_REQUIRED_ERROR}</S.Error>
                )}
              </S.ErrorWrapper>
            </S.InputWrapper>
            <S.InputWrapper>
              <StyledTextField
                label={COST_LABEL}
                id={`activities[${index}].cost`}
                padding='dense'
                size='small'
                variant='outlined'
                width={160}
                error={!!errors.activities?.[index]?.cost}
                {...register(
                  `activities[${index}].cost` as ['activities'][number],
                  {
                    required: true,
                    min: {
                      value: 0,
                      message: FIELD_MIN_ZERO_ERROR,
                    },
                    onChange: (e) => {
                      let value = e.target.value;
                      value = value.replace(',', '.');
                      e.target.value = value;
                      calculateAllIn();
                    },
                  },
                )}
              />
              <S.ErrorWrapper maxWidth='160px'>
                {errors.activities?.[index]?.cost && (
                  <S.Error>
                    {errors.activities?.[index]?.cost?.message ||
                      FIELD_REQUIRED_ERROR}
                  </S.Error>
                )}
              </S.ErrorWrapper>
            </S.InputWrapper>
            <S.InputWrapper>
              <Controller
                name={`activities[${index}].currency` as ['activities'][number]}
                control={control}
                rules={fieldIsRequired}
                render={(props) => (
                  <CurrencyAutocomplete
                    formProps={props}
                    error={!!errors.activities?.[index]?.currency}
                    index={index}
                    onCurrencyChange={currencyChangeHandler}
                  />
                )}
              />
              <S.ErrorWrapper>
                {errors.activities?.[index]?.currency && (
                  <S.Error>{FIELD_REQUIRED_ERROR}</S.Error>
                )}
              </S.ErrorWrapper>
            </S.InputWrapper>
            {index > 0 && fields.length > 1 && (
              <S.RemoveActivityButton
                type='button'
                onClick={removeActivityHandler(index)}
              >
                X
              </S.RemoveActivityButton>
            )}
          </S.FieldGroup>
        ))}
        <S.FieldGroup>
          <S.AddNewActivityButton type='button' onClick={addNewActivityHandler}>
            {ADD_NEW_ACTIVITY_BUTTON}
          </S.AddNewActivityButton>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='rateAllIn'>
            {`${RATE_ALL_IN_LABEL}`}
            <S.StyledCurrency changed={currencyChanged}>
              {freightCurrency ? `(${freightCurrency})` : ''}
            </S.StyledCurrency>
          </S.FieldLabel>
          <S.InputWrapper>
            <StyledTextField
              id='rateAllIn'
              padding='dense'
              size='small'
              variant='outlined'
              width={280}
              value={allInValue && Number(allInValue).toFixed(2)}
              disabled
              error={!!errors.rateAllIn}
              {...register('rateAllIn')}
            />
            <S.ErrorWrapper>
              {errors.rateAllIn && (
                <S.Error>{errors?.rateAllIn?.message}</S.Error>
              )}
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='expirationDateFrom'>
            {EXPIRATION_DATE_FROM_LABEL}
          </S.FieldLabel>
          <S.InputWrapper>
            <StyledTextField
              id='expirationDateFrom'
              padding='dense'
              size='small'
              variant='outlined'
              type='date'
              width={280}
              error={!!errors.expirationDateFrom}
              {...register('expirationDateFrom')}
            />
            <S.ErrorWrapper>
              {errors.expirationDateFrom && (
                <S.Error>
                  {errors?.expirationDateFrom?.message || FIELD_REQUIRED_ERROR}
                </S.Error>
              )}
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='expirationDateTo'>
            {EXPIRATION_DATE_TO_LABEL}
          </S.FieldLabel>
          <S.InputWrapper>
            <StyledTextField
              id='expirationDateTo'
              padding='dense'
              size='small'
              variant='outlined'
              type='date'
              width={280}
              error={!!errors.expirationDateTo}
              {...register(
                'expirationDateTo',
                expirationDateToValidationRules(expirationDateFromValue),
              )}
            />
            <S.ErrorWrapper>
              {errors.expirationDateTo && (
                <S.Error>
                  {errors?.expirationDateTo?.message || FIELD_REQUIRED_ERROR}
                </S.Error>
              )}
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='daysOff'>{DAYS_OFF_LABEL}</S.FieldLabel>
          <S.InputWrapper>
            <StyledTextField
              id='daysOff'
              padding='dense'
              size='small'
              variant='outlined'
              width={280}
              {...register('daysOff')}
            />
            <S.ErrorWrapper>
              {errors.daysOff && <S.Error>{errors?.daysOff?.message}</S.Error>}
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.FieldGroup>
          <S.FieldLabel htmlFor='internalNote'>
            {INTERNAL_NOTE_LABEL}
          </S.FieldLabel>
          <S.InputWrapper>
            <StyledTextField
              id='internalNote'
              padding='dense'
              size='small'
              variant='outlined'
              width={280}
              {...register('internalNote')}
            />
            <S.ErrorWrapper>
              {errors.internalNote && (
                <S.Error>{errors?.internalNote?.message}</S.Error>
              )}
            </S.ErrorWrapper>
          </S.InputWrapper>
        </S.FieldGroup>
        <S.ActionsContainer>
          <StyledButton
            $primary
            type='submit'
            disabled={(!_.isEmpty(errors) || submitButtonDisabled) as boolean}
          >
            {ADD_RATE_BUTTON}
          </StyledButton>
          <StyledButton onClick={cancelButtonHandler}>
            {CANCEL_BUTTON}
          </StyledButton>
        </S.ActionsContainer>
      </S.Form>
    </Box>
  );
};
